import React, { useState } from "react"
import { useDispatch } from "react-redux"
import { Button } from "../../shared/Button"
import { TicketFill } from "react-bootstrap-icons"
import {
  getTicketAvailabilityMessage,
  isEventOver,
  formatPrice,
} from "../../../util/ticketUtils"
import { AlternatePricingDisplay } from "../AlternatePricingDisplay"
import LoggedOutCheckoutModal from "../LoggedOutCheckoutModal"
import { openAuthDialog } from "../../Authentication"

const PRICING_TYPE_FREE = "free"

interface TicketPurchaseComponentLoggedOutProps {
  postId: number
  bandId: number
  bandName: string
  eventTitle?: string
  ticketPrice: number
  maxTickets: number
  ticketsSold: number
  eventEndDate: string
  ticketPriceAtDoor?: number
  concert?: { [key: string]: unknown } | null
}

export const TicketPurchaseComponentLoggedOut: React.FC<
  TicketPurchaseComponentLoggedOutProps
> = ({
  postId,
  bandName,
  eventTitle = "",
  ticketPrice,
  maxTickets,
  ticketsSold,
  eventEndDate,
  concert,
}) => {
  const isSlidingScale = (concert as any)?.pricing_type === "sliding_scale"
  const isFreeTicket = (concert as any)?.pricing_type === PRICING_TYPE_FREE
  const ticketPriceMax = (concert as any)?.ticket_price_max as number | null ?? null
  const [showModal, setShowModal] = useState(false)
  const eventOver = isEventOver(eventEndDate)
  const dispatch = useDispatch()

  const availabilityMessage = getTicketAvailabilityMessage(
    maxTickets,
    ticketsSold,
    eventEndDate,
  )

  // Free tickets need an account (so we know who to send it to / let them
  // manage it later) but not a card - send logged-out fans through real
  // signup instead of LoggedOutCheckoutModal's guest-checkout-with-card flow.
  // Flagging the URL first means that once signup finishes (a full page
  // navigation back to current_location - see RegistrationForm), this page
  // reloads already authenticated with ?auto-open-ticket-purchase=true still
  // present, and TicketPurchaseComponent's existing auto-claim effect
  // (see ConcertListItemExpanded) picks it up and claims the ticket.
  const claimFreeTicketViaSignup = () => {
    const url = new URL(window.location.href)
    url.searchParams.set("auto-open-ticket-purchase", "true")
    window.history.replaceState({}, "", url.toString())

    dispatch(
      openAuthDialog({
        modalState: "register",
        defaultBandType: "fan",
        explanation: eventTitle
          ? `Sign up to claim your free ticket for ${eventTitle}!`
          : "Sign up to claim your free ticket!",
      }),
    )
  }

  return (
    <div>
      <div className="grid grid-cols-12 my-3">
        <div className="lg:col-span-6 col-span-12 text-base">
          <div className="flex flex-col items-start space-y-2">
            {availabilityMessage && (
              <div
                className={`text-sm ${availabilityMessage === "Low ticket warning!" ? "text-red-600 font-semibold" : "text-gray-600"}`}
              >
                {availabilityMessage}
              </div>
            )}

            <Button
              className="w-auto px-4"
              onClick={() =>
                isFreeTicket ? claimFreeTicketViaSignup() : setShowModal(true)
              }
              disabled={eventOver}
            >
              {eventOver
                ? "Event Ended"
                : isFreeTicket
                  ? "Get Free Ticket / RSVP"
                  : isSlidingScale && ticketPriceMax
                    ? `Buy ${formatPrice(ticketPrice)}–${formatPrice(ticketPriceMax)} Ticket`
                    : `Buy ${formatPrice(ticketPrice)} Ticket`}
              {!eventOver && <TicketFill className="inline ml-2" />}
            </Button>
            <AlternatePricingDisplay concert={concert ?? null} />
          </div>
        </div>
      </div>

      {showModal && (
        <LoggedOutCheckoutModal
          postId={postId}
          bandName={bandName}
          eventTitle={eventTitle}
          ticketPrice={ticketPrice}
          onClose={() => setShowModal(false)}
          concert={concert ?? null}
          maxTickets={maxTickets}
          ticketsSold={ticketsSold}
        />
      )}
    </div>
  )
}

export default TicketPurchaseComponentLoggedOut
